import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pascalCase } from 'pascal-case';
import { useParams } from 'react-router';
import styled from 'styled-components';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { RedStar } from '../Permitting/Documents/AddPermittingDocumentPopup';
import Dropdown from '../Permitting/Documents/Dropdown';
import { RootState } from '../../../redux/root';
import { setPermittingDocErrors, setWikiNote } from '../../../redux/slices/wikiSlice';
import { WikiNoteType } from '../../../api/jurisdiction';
import { useSaveWikiNoteMutation, useUpdateWikiNoteMutation } from '../../../redux/rtkQuery/apiWikiNoteSlice';
import { useGetVocabularyQuery } from '../../../redux/rtkQuery/apiSlice';

// Register style-based attributors so font, size, and color produce inline CSS,
// which renders correctly in the notes table without depending on quill.snow.css.
const FONT_WHITELIST = ['Arial', 'Georgia', 'Tahoma', 'Verdana', 'Helvetica', 'Courier', 'sans-serif', 'serif', 'monospace'];
const SIZE_WHITELIST = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px'];

let quillAttributorsRegistered = false;
const registerQuillAttributors = () => {
  if (quillAttributorsRegistered) return;
  const SizeStyle = Quill.import('attributors/style/size');
  SizeStyle.whitelist = SIZE_WHITELIST;
  Quill.register(SizeStyle, true);

  const FontStyle = Quill.import('attributors/style/font');
  FontStyle.whitelist = FONT_WHITELIST;
  Quill.register(FontStyle, true);

  Quill.register(Quill.import('attributors/style/color'), true);
  Quill.register(Quill.import('attributors/style/background'), true);
  quillAttributorsRegistered = true;
};
registerQuillAttributors();

const isQuillEmpty = (html: string) => {
  if (!html) return true;
  const stripped = html.replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, '').trim();
  return stripped.length === 0;
};

const AddNotePopup = ({ setShowAddNote }) => {
  const { wikiNote: data, permittingDocErrors: errors } = useSelector((s: RootState) => s.wikiSlice);
  const dispatch = useDispatch();
  const [saveWikiNote] = useSaveWikiNoteMutation();
  const [updateWikiNote] = useUpdateWikiNoteMutation();
  const { data: vocaTopic } = useGetVocabularyQuery(15);
  const { data: vocaCategory } = useGetVocabularyQuery(16);
  // @ts-ignore
  const { wikiId } = useParams();

  useEffect(() => {
    if (data) {
      if (!vocaTopic || !vocaCategory) {
        return;
      }

      if (data.isEditing && typeof data.category === 'number') {
        const topic = vocaTopic.results.find(vt => vt.id === data.topic);
        const category = vocaCategory.results.find(ct => ct.id === data.category);
        const payload = {
          ...data,
          // Note may be HTML (new) or plain text with line breaks (legacy);
          // convert legacy newlines to <br/> so the rich editor preserves them.
          note: data.note ? data.note.replace(/\r\n/g, '<br/>') : '',
          topic: topic
            ? {
                label: topic.title,
                value: topic.id,
              }
            : undefined,
          category: category
            ? {
                label: category.title,
                value: category.id,
              }
            : undefined,
        };
        dispatch(setWikiNote(payload));
      }
    }
  }, [data, vocaTopic, vocaCategory]);

  const onNoteChange = (value: string) => {
    dispatch(setWikiNote({ ...data, note: value }));
    const _errors = { ...errors };
    delete _errors.note;
    dispatch(setPermittingDocErrors(_errors));
  };

  const onChange = (e, name) => {
    dispatch(
      setWikiNote({
        ...data,
        [name]: e,
      }),
    );
    const _errors = { ...errors };
    delete _errors[name];
    dispatch(setPermittingDocErrors(_errors));
  };

  const onSubmit = async () => {
    const keys = ['topic', 'category', 'note'];
    let errorsFound = false;
    const _errors = {};
    keys.forEach(k => {
      const value = data[k];
      const isEmpty = k === 'note' ? isQuillEmpty(value as string) : !value;
      if (isEmpty) {
        errorsFound = true;
        _errors[k] = {
          message: `${pascalCase(k)} is required!`,
        };
      }
    });
    if (errorsFound) {
      dispatch(setPermittingDocErrors(_errors));
      return;
    }

    const formData = new FormData();
    formData.append('jurisdiction', wikiId);
    formData.append('topic', data.topic.value);
    formData.append('category', data.category.value);
    formData.append('note', data.note);

    if (data.isEditing) {
      const _frm = {
        id: data.id,
        frm: formData,
      };
      formData.append('id', `${data.id}`);
      await updateWikiNote(_frm);
    } else {
      await saveWikiNote(formData);
    }

    const _wikiNote: WikiNoteType = {
      topic: undefined,
      category: undefined,
      note: '',
      isEditing: false,
    };
    dispatch(setWikiNote(_wikiNote));
    setShowAddNote(false);
  };

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ font: FONT_WHITELIST }, { size: SIZE_WHITELIST }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
      ],
    }),
    [],
  );

  const quillFormats = ['font', 'size', 'bold', 'italic', 'underline', 'color', 'background', 'list', 'bullet'];

  return (
    <Form>
      <Dropdown
        showValidation
        label="Topic"
        entity={15}
        name="topic"
        value={data ? data.topic : {}}
        onChange={e => onChange(e, 'topic')}
      />
      <Dropdown
        showValidation
        label="Category"
        entity={16}
        name="category"
        value={data ? data.category : {}}
        onChange={e => onChange(e, 'category')}
      />

      <FieldGroup>
        <FieldLabel>
          Note<RedStar>*</RedStar>
        </FieldLabel>
        <EditorWrap hasError={!!(errors && errors.note)}>
          <ReactQuill
            theme="snow"
            value={data.note || ''}
            onChange={onNoteChange}
            placeholder="Write your note here..."
            modules={quillModules}
            formats={quillFormats}
          />
        </EditorWrap>
        {errors && errors.note && <FieldError>{errors.note.message}</FieldError>}
      </FieldGroup>

      <Actions>
        <SecondaryButton type="button" onClick={() => setShowAddNote(false)}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onSubmit}>
          {data.isEditing ? 'Update' : 'Save'}
        </PrimaryButton>
      </Actions>
    </Form>
  );
};

export default AddNotePopup;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol'`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 360px;
  font-family: ${FONT_STACK};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const FieldError = styled.div`
  font-size: 0.75rem;
  color: var(--danger-500);
`;

const fontPickerLabels = FONT_WHITELIST.map(
  font => `
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='${font}']::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='${font}']::before {
      content: '${font}';
      font-family: ${font.includes(' ') ? `'${font}'` : font};
    }
  `,
).join('');

const sizePickerLabels = SIZE_WHITELIST.map(
  size => `
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='${size}']::before,
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='${size}']::before {
      content: '${size}';
    }
  `,
).join('');

const EditorWrap = styled.div<{ hasError?: boolean }>`
  ${fontPickerLabels}
  ${sizePickerLabels}

  .ql-toolbar.ql-snow {
    border: 1px solid ${({ hasError }) => (hasError ? 'var(--danger-500)' : 'var(--border)')};
    border-radius: 8px 8px 0 0;
    background: var(--neutral-50);
    padding: 6px 8px;
  }

  .ql-container.ql-snow {
    border: 1px solid ${({ hasError }) => (hasError ? 'var(--danger-500)' : 'var(--border)')};
    border-top: 0;
    border-radius: 0 0 8px 8px;
    background: var(--neutral-0);
    font-family: ${FONT_STACK};
    font-size: 0.875rem;
  }

  .ql-editor {
    min-height: 160px;
    color: var(--mainText);
    line-height: 1.5;
  }

  .ql-editor.ql-blank::before {
    color: var(--neutral-400);
    font-style: normal;
  }

  .ql-snow .ql-picker.ql-font {
    width: 130px;
  }

  .ql-snow .ql-picker.ql-size {
    width: 80px;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
`;

const PrimaryButton = styled.button`
  min-width: 120px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SecondaryButton = styled.button`
  min-width: 100px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
